import React, { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { selectDataTableAppliedSearchName, selectDataTableFilters, selectDataTablePage } from '@/redux/selectors/dataTableSelectors';
import { applyFilters, setCurrentPage } from '@/redux/slices/dataTableSlice';
import { deepCopy, isFalsey } from '../../../_utils/utils';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { isEqual } from 'lodash';
import { FiltersBarProps } from '@/components/_common/Filters/FiltersBar';

export const cleanSavedFilters = (filters, removeNullsAndEmpties = true) => {
  const copy = deepCopy(filters);

  Object.keys(filters).forEach((key) => {
    const value = filters[key];

    if (
      removeNullsAndEmpties &&
      (value === null || (Array.isArray(value) && !value.length) || (typeof value === 'object' && !Object.keys(value).length))
    ) {
      delete copy[key];
    } else if (legacyKeys[key]) {
      // replace legacy keys with new keys
      delete copy[key];
      copy[legacyKeys[key]] = value;
    }
  });

  return copy;
};

const legacyKeys = {
  name: 'name_filter',
  job_type: 'job_type_filter',
  status: 'status_filter',
  stage: 'stage_filter'
};

const QuerystringController =
  (Component: React.FC<FiltersBarProps>) =>
  ({ ...props }: FiltersBarProps) => {
    const currentFilters = useAppSelector(selectDataTableFilters);
    const appliedSearchName = useAppSelector(selectDataTableAppliedSearchName);
    const currentPage = useAppSelector(selectDataTablePage);
    const history = useHistory();
    const { search } = useLocation();
    const dispatch = useAppDispatch();
    const query = React.useMemo(() => new URLSearchParams(search), [search]);
    const idQuery = query.get('id');

    useEffect(() => {
      const existingParams = new URLSearchParams(window.location.search);
      const calendarStartDate = existingParams.get('calendarStartDate'); // used by `AvailsCalendar`
      const calendarView = existingParams.get('calendarView'); // used by `AvailsCalendar`
      const searchParams = new URLSearchParams();
      if (idQuery) searchParams.set('id', idQuery);

      if (calendarStartDate) searchParams.set('calendarStartDate', calendarStartDate);
      if (calendarView) searchParams.set('calendarView', calendarView);

      const cleanedFilters = cleanSavedFilters(currentFilters);

      if (Object.keys(cleanedFilters)?.length) {
        searchParams.append('filters', JSON.stringify(cleanedFilters));
      }
      if (appliedSearchName) {
        searchParams.append('appliedSearchName', appliedSearchName);
      }
      if (currentPage) {
        searchParams.append('page', currentPage);
      }

      history.replace({ search: '?' + searchParams.toString() });
    }, [currentFilters, currentPage]);

    useEffect(() => {
      const filtersOnQuerystring = query.get('filters');
      const appliedSearchNameOnQuerystring = query.get('appliedSearchName');
      const page = query.get('page');
      const filters = JSON.parse(filtersOnQuerystring);
      applyExistingFilters(filters, appliedSearchNameOnQuerystring, page);
    }, []);

    const applyExistingFilters = (filters, name, page) => {
      if (!isFalsey(filters)) {
        const cleanedFilters = cleanSavedFilters(filters);
        if (!isEqual(cleanSavedFilters(currentFilters), cleanedFilters)) {
          dispatch(applyFilters({ filters: cleanedFilters, name }));
        }
      }

      dispatch(setCurrentPage(page ? +page : 1));
    };

    return <Component {...props} />;
  };

export default QuerystringController;
